import { Controller } from "@hotwired/stimulus";
import { effect, signal } from "@preact/signals-core";
import type MapBoard from "./map_board_controller";
import type MapDice from "./map_dice_controller";

const IDLE_MS = 3000;

// Connects to data-controller="map-table", beside map-board on the table view:
// the players' view on a TV. It keeps the screen awake, goes full screen,
// turns the map around for a screen lying flat, and hides its few buttons and
// the pointer while nobody touches it. On the table link, which never writes,
// the turn stays on this screen, and a table that lost its access stops the
// dice feed's stream too.
export default class extends Controller<HTMLElement> {
	static targets = ["canvas", "chrome", "fullscreen", "rotate"];
	declare readonly canvasTarget: HTMLElement;
	declare readonly chromeTarget: HTMLElement;
	declare readonly fullscreenTargets: HTMLElement[];
	declare readonly rotateTargets: HTMLElement[];
	// The table link's own turn, over the scene's rotation; null follows the scene.
	turnedHere = signal<boolean | null>(null);
	connected = false;
	idleTimer?: ReturnType<typeof setTimeout>;
	wakeLock?: WakeLockSentinel;
	disposeRotation?: () => void;
	disposeAccess?: () => void;

	connect() {
		this.connected = true;
		this.keepAwake();
		document.addEventListener("visibilitychange", this.keepAwake);
		document.addEventListener("fullscreenchange", this.showFullscreen);
		for (const type of ["pointermove", "pointerdown", "keydown"]) window.addEventListener(type, this.wake);
		for (const button of this.fullscreenTargets) button.hidden = !document.fullscreenEnabled;
		this.showFullscreen();
		this.wake();
		if (this.board?.state) this.ready();
	}

	disconnect() {
		this.connected = false;
		clearTimeout(this.idleTimer);
		this.disposeRotation?.();
		this.disposeAccess?.();
		document.removeEventListener("visibilitychange", this.keepAwake);
		document.removeEventListener("fullscreenchange", this.showFullscreen);
		for (const type of ["pointermove", "pointerdown", "keydown"]) window.removeEventListener(type, this.wake);
		this.wakeLock?.release().catch(() => {});
		this.wakeLock = null;
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	ready() {
		this.disposeRotation?.();
		// The toasts, the dice cards and the thrown dice turn with the map, to be read from the same side.
		this.disposeRotation = effect(() => {
			const turned = this.turnedHere.value ?? this.board.state.view.value.scene?.table_view?.rotation === 180;
			this.canvasTarget.style.rotate = turned ? "180deg" : "";
			this.board.toastsTarget.toggleAttribute("data-turned", turned);
			for (const selector of ['[data-map-dice-target="cards"]', '[data-map-dice-target="layer"]', "[data-sound-prompt]"])
				this.element.querySelector(selector)?.toggleAttribute("data-turned", turned);
			for (const button of this.rotateTargets) button.setAttribute("aria-pressed", String(turned));
		});
		this.disposeAccess?.();
		this.disposeAccess = effect(() => {
			if (this.board.sync.status.value === "rejected") this.revoke();
		});
	}

	// The rolls feed (the link's frame, or the signed-in view's island, whose removal releases its client) and what it was playing go.
	revoke() {
		this.element.querySelector("#drawer-right")?.remove();
		this.element.querySelector('[data-live-island-component-value="map_rolls"]')?.remove();
		(this.application.getControllerForElementAndIdentifier(this.element, "map-dice") as MapDice | null)?.stop();
	}

	// Screen Wake Lock exists only in secure contexts and some browsers; the
	// page is the same without it. A lock ends whenever the tab is hidden; one
	// granted after the view left is let go at once, and overlapping requests
	// keep only the newest.
	keepAwake = async () => {
		if (document.visibilityState !== "visible" || !navigator.wakeLock) return;
		try {
			const lock = await navigator.wakeLock.request("screen");
			if (!this.connected) return lock.release().catch(() => {});
			this.wakeLock?.release().catch(() => {});
			this.wakeLock = lock;
		} catch {}
	};

	wake = () => {
		delete this.element.dataset.idle;
		clearTimeout(this.idleTimer);
		this.idleTimer = setTimeout(() => {
			if (!this.chromeTarget.querySelector(":focus-visible")) this.element.dataset.idle = "";
		}, IDLE_MS);
	};

	toggleFullscreen() {
		if (document.fullscreenElement) document.exitFullscreen?.();
		else document.documentElement.requestFullscreen?.().catch(() => {});
	}

	showFullscreen = () => {
		for (const button of this.fullscreenTargets) button.setAttribute("aria-pressed", String(Boolean(document.fullscreenElement)));
	};

	rotate() {
		const scene = this.board?.state?.view.peek().scene;
		if (!scene) return;
		if (this.board.readOnlyValue) {
			this.turnedHere.value = !(this.turnedHere.peek() ?? scene.table_view?.rotation === 180);
			return;
		}
		this.board.updateScene({ table_view: { ...scene.table_view, rotation: scene.table_view?.rotation === 180 ? 0 : 180 } });
	}
}
